import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { throwError } from 'rxjs';

import { environment } from '@envs/environment';

/** Valores que llegan a una URL cuando un id no existe en sesión o no se cargó. */
const INVALID_VALUES = new Set(['null', 'undefined', 'nan']);

/**
 * Corta, antes de enviarla, la petición al API que lleva en la ruta o en la
 * query un id 'null', 'undefined', 'NaN' o vacío.
 *
 * .NET Core 3.1 dejaba pasar algunos de estos casos; .NET 10 los rechaza con
 * 400 al enlazar Guid, int o enum. Cortarlos aquí da un error claro (qué URL y
 * qué parámetro) en lugar de un 400 genérico del backend.
 */
export const apiRequestGuardInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(environment.api)) {
    return next(req);
  }
  const problem = findInvalidParam(req);
  if (!problem) {
    return next(req);
  }
  const message = `Invalid ${problem} in ${req.method} ${req.urlWithParams}`;
  console.error(`[api-request-guard] ${message}`);
  return throwError(
    () =>
      new HttpErrorResponse({
        url: req.urlWithParams,
        status: 400,
        statusText: 'Invalid request',
        // Misma forma que la respuesta de error del backend (ErrorHandlerMiddleware)
        error: { Succeeded: false, Menssage: message, NativeErrorCode: 0 },
      }),
  );
};

/** Devuelve qué parámetro es inválido, o null si la petición está bien. */
export function findInvalidParam(req: HttpRequest<unknown>): string | null {
  const [base, query = ''] = req.url.split('?');
  const path = base.slice(environment.api.length);

  // Un segmento vacío (//) sale de concatenar un id vacío
  const segments = path.split('/').slice(1);
  const badSegment = segments.findIndex(
    (s, i) => isInvalid(s) || (s === '' && i < segments.length - 1),
  );
  if (badSegment >= 0) {
    return `route segment ${badSegment + 1} ('${segments[badSegment]}')`;
  }

  const fromUrl = new URLSearchParams(query);
  for (const [key, value] of fromUrl) {
    if (isInvalid(value)) {
      return `query param '${key}' ('${value}')`;
    }
  }
  for (const key of req.params.keys()) {
    const value = req.params.getAll(key)?.find(isInvalid);
    if (value !== undefined) {
      return `query param '${key}' ('${value}')`;
    }
  }
  return null;
}

function isInvalid(value: string): boolean {
  let decoded = value;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    // Un '%' suelto no es un id inválido; se deja pasar tal cual
  }
  return INVALID_VALUES.has(decoded.trim().toLowerCase());
}
